<template>
	<div class="grid grid-cols-1 gap-20">
		<div class="pt-8 component-container">
			<Checklist
				:image="ANIMATED_TRANSMIT_IMAGE"
				:features="residentialFeaturesChecklist"
				:title="$t('residential.features-title')"
			/>
		</div>
		<div class="component-container">
			<LearnMore
				v-for="(section, index) in sections"
				:key="section.title"
				:data="section"
				:position="getPosition('left', index)"
			/>
		</div>
		<div class="w-full px-8 border-t-2 component-container md:max-w-2xl" />
		<LazyInstallationEventList class="px-8 component-container" />
		<LazyCarouselTestimonial />
		<LazyFaq
			class="px-8 -mt-4 component-container"
			:faqs="faqs"
		/>
	</div>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import {
	COMPANY_NAME,
	COMPANY_PHONE,
	BASE_URL,
	STORYBLOK_VERSION
} from '@/constants/index.js'
import {
	JSON_LD_LOGO,
	JSON_LD_HAS_OFFER_CATALOG,
	JSON_LD_ORGANIZATION_AREA_SERVED
} from '@/constants/json-ld'
import type { FaqProps } from '@/components/faq/types'
import type { ChecklistProps } from '@/components/checklist/types'
import { mapLearnMore, getPosition } from '@/utils/learnMoreMap'
import { useStoryblokWithErrorReporting } from '@/composables/useStoryblok'

const route = useRoute()

const { getStory } = useStoryblokWithErrorReporting()

const { t, tm, locale } = useI18n({
	useScope: 'global'
})

useHead({
	title: t('residential.meta-title'),
	meta: [
		{ name: 'description', content: t('residential.meta-description', { COMPANY_NAME }) },
		{ name: 'url', content: `${BASE_URL}/residential-internet` }
	],
	link: [
		{
			rel: 'icon',
			type: 'image/x-icon',
			href: '/favicon.ico'
		}
	]
})

const story = await getStory('residential-internet', {
	version: STORYBLOK_VERSION,
	language: locale.value
})

const { title = '', description = '' } = story?.value?.content?.metatags

useSeoMeta({
	title: () => title,
	ogTitle: () => title,
	description: () => description,
	ogDescription: () => description,
	ogImage: () => '/images/logo.svg',
	ogUrl: () => BASE_URL,
	twitterTitle: () => title,
	twitterDescription: () => description,
	twitterImage: () => '/images/logo.svg',
	twitterCard: () => 'summary_large_image'
})

const sections = mapLearnMore(story?.value?.content?.learnMoreContent)

const faqs: FaqProps[] = tm('faqs')
const residentialFeaturesChecklist: ChecklistProps[] = tm('residential.features')

const ANIMATED_TRANSMIT_IMAGE = {
	id: 'residential-internet-transmit-image',
	src: '/images/laptop-girl.webp',
	srcset: '/images/laptop-girl-sm.webp 400w, /images/laptop-girl.webp 800w',
	alt: t('residential.image-alt')
}

definePageMeta({
	showAddressSearch: true,
	useDefaultHeroContent: true
})

useJsonld({
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: BASE_URL
				}, {
					'@type': 'ListItem',
					position: 2,
					name: 'Residential Internet',
					item: `${BASE_URL}${route.fullPath}`
				}
			]
		},
		{
			'@type': 'Service',
			name: COMPANY_NAME,
			logo: JSON_LD_LOGO,
			provider: {
				'@type': 'Organization',
				name: COMPANY_NAME,
				telephone: COMPANY_PHONE
			},
			serviceType: 'High-speed Internet',
			serviceOutput: 'Internet service',
			areaServed: JSON_LD_ORGANIZATION_AREA_SERVED,
			hasOfferCatalog: JSON_LD_HAS_OFFER_CATALOG
		}
	]
})

</script>
